import {
  combineReducers,
  configureStore,
  UnknownAction
} from '@reduxjs/toolkit';
import adminReducer from 'features/admin/admin-slice';
import aiReducer from 'features/ai/ai-slice';
import { emptyApi } from 'features/api/empty-api';
import authReducer from 'features/auth/auth-slice';
import chatbotReducer from 'features/chatbot/chatbot-slice';
import configurationsReducer from 'features/configurations/configurations-slice';
import dataReducer from 'features/data/data-slice';
import ductedCompatibilityReducer from 'features/ducted-compatibility/ducted-compatibility-slice';
import projectReducer from 'features/project/project-slice';
import ptacSelectionReducer from 'features/ptac-selection/ptac-selection-slice';
import { structureHistoryMiddleware } from 'features/structure/structure-history-middleware';
import structureHistoryReducer from 'features/structure/structure-history-slice';
import { structureReconcileMiddleware } from 'features/structure/structure-reconcile-middleware';
import structureReducer from 'features/structure/structure-slice';
import systemsReducer from 'features/systems/systems-slice';
import uiReducer from 'features/ui/ui-slice';
import { actionBreadcrumbsMiddleware } from 'utils/error-log/action-breadcrumbs-middleware';
import {
  applyReduxSnapshot,
  hydrateReduxSnapshot
} from 'utils/error-log/redux-snapshot';

const combinedReducer = combineReducers({
  [emptyApi.reducerPath]: emptyApi.reducer,
  admin: adminReducer,
  ai: aiReducer,
  auth: authReducer,
  chatbot: chatbotReducer,
  configurations: configurationsReducer,
  data: dataReducer,
  ductedCompatibility: ductedCompatibilityReducer,
  project: projectReducer,
  ptacSelection: ptacSelectionReducer,
  structure: structureReducer,
  structureHistory: structureHistoryReducer,
  systems: systemsReducer,
  ui: uiReducer
});

export type RootState = ReturnType<typeof combinedReducer>;

const rootReducer = (
  state: RootState | undefined,
  action: UnknownAction
): RootState => {
  if (state && hydrateReduxSnapshot.match(action)) {
    const next = applyReduxSnapshot(state, action.payload);
    if (!next) {
      return state;
    }
    return combinedReducer(next, action);
  }
  return combinedReducer(state, action);
};

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: { ignoredActions: [hydrateReduxSnapshot.type] }
    })
      .concat(actionBreadcrumbsMiddleware)
      .concat(structureHistoryMiddleware)
      .concat(structureReconcileMiddleware)
      .concat(emptyApi.middleware);
  }
});

export type AppDispatch = typeof store.dispatch;
export default store;
